/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
	Command,
	GameRound,
	GameState,
	PassCardsData,
	PlayCardData,
	Player,
} from './definitions';
import { GameAction, PassDirection, RoundStage } from './definitions';
import type { ActionData } from './general_definitions';
import { Suit } from './general_definitions';
import {
	assumeType,
	decodeMovePile,
	deepCopy,
	getCardsInHand,
	getCardsInPile,
	getCardString,
	shuffle,
	sortCards,
} from './general_utils';
import { Rules } from './rules';
import { createGameRound, createGameState, createTrick } from './state';
import {
	calculateCardPoints,
	calculateCardPointsHeartsOnly,
	checkForQueenSpades,
	dealCards,
	getPassDirectionFromGameState,
	getPassPositionByGameState,
	getPlayerHandName,
	getPlayerPassHandName,
	getScorePileName,
	getTrickPileName,
	getTrickWinner,
	isRoundOver,
	makePassCommand,
	makePlayCardCommand,
} from './utils';

export function action(gameAction: GameAction | Command, gs: GameState, position?: number, data?: ActionData): GameState {
	if (typeof gameAction !== 'string') {
		assumeType<Command>(gameAction);
		data = gameAction.data;
		position = gameAction.position;
		gameAction = gameAction.command as GameAction;
	}

	if (!Rules.can(gameAction, gs, position, data)) {
		return gs;
	}

	//Rules say we can do this action, apply the action to the gameState
	const gameState = deepCopy<GameState>(gs);

	switch (gameAction) {
		case GameAction.PassCards:
			assumeType<PassCardsData>(data);
			return handlePassCardsAction(data, gameState, position!);
		case GameAction.PlayCard:
			assumeType<PlayCardData>(data);
			return handlePlayCardAction(data, gameState, position!);
		case GameAction.Deal:
			return handleDeal(gameState);
		case GameAction.NextStage:
			return handleNextStage(gameState);

		case GameAction.Togglecard:
			assumeType<PlayCardData>(data);
			return handleToggleCardAction(data, gameState);
		case GameAction.NewGame:
			return handleNewGame(data as Player[] | undefined, gameState);
		case GameAction.LoadGame:
			return handleLoadGame(data as Player[] | undefined, gameState);
		default:
			return gs;
	}
}

//All mutation functions assume that they are mutating the sent state
function handleLoadGame(players: Player[] | undefined, gameState: GameState): GameState {
	if (!gameState) {
		return handleNewGame(players, gameState);
	}

	gameState = gameState ?? createGameState(4);

	if (players && players.length > 0) {
		gameState.players = players.map((p: Player) => {
			return {
				avatar: p.avatar,
				name: p.name,
				position: p.position,
				algorithm: p.algorithm,
				isLocal: p.isLocal,
				delay: p.delay,
				skill: p.skill,
			};
		});
	}

	return gameState;
}

function handleNewGame(players: Player[] | undefined, gameState: GameState): GameState {
	//reset match and retain the players and level

	if (!players) {
		players = gameState.players;
	}

	//carry over the game state level
	const level = gameState?.level ?? '';

	//create a new game state
	gameState = createGameState(4);

	//carry over level if it exists
	gameState.level = level;

	//carry over players if they exist
	if (players && players.length > 0) {
		gameState.players = players.map((p: Player) => {
			return {
				avatar: p.avatar,
				name: p.name,
				position: p.position,
				algorithm: p.algorithm,
				isLocal: p.isLocal,
				delay: p.delay,
				skill: p.skill,
			};
		});
	}

	return gameState;
}

function handlePassCardsAction(data: PassCardsData, gameState: GameState, position: number): GameState {
	const newHandName = getPlayerPassHandName(position);

	for (const c of data.cards) {
		const card = gameState.deck.find((cd) => {
			return getCardString(cd) === c;
		});

		if (card) {
			card.pile = newHandName;
		}
	}

	const passCardCount = gameState.deck.filter((c) => c.pile.indexOf('pass') > -1).length;

	if (passCardCount === 3 * gameState.playerCount) {
		gameState.roundStage = RoundStage.PassEnd;
	}

	return gameState;
}

function handlePlayCardAction(data: PlayCardData, gameState: GameState, position: number): GameState {
	const { card: cardString } = data;
	const card = gameState.deck.find((c) => getCardString(c) === cardString);
	const currentRound = gameState.rounds.at(-1);
	const currentTrick = currentRound?.tricks.at(-1);

	if (!card || typeof position === 'undefined' || !currentRound || !currentTrick) {
		return gameState;
	}

	card.pile = getTrickPileName(position);
	currentTrick.cardsPlayed[position] = getCardString(card);
	++gameState.currentPlayer;

	if (gameState.currentPlayer > gameState.playerCount - 1) {
		gameState.currentPlayer = 0;
	}

	if (currentTrick.cardsPlayed.filter(Boolean).length >= gameState.playerCount) {
		gameState = handleTrickEnded(gameState);
	}

	if (card.suit === Suit.Hearts) {
		gameState.heartsBroken = true;
	}

	return gameState;
}

function handleDeal(gameState: GameState): GameState {
	shuffle(gameState.deck);
	gameState = dealCards(gameState);
	gameState.currentPlayer = get2CPlayer(gameState);
	const passDirection = getPassDirectionFromGameState(gameState);
	gameState.roundStage = passDirection !== PassDirection.None ? RoundStage.Pass : RoundStage.PassEnd;
	return gameState;
}

function get2CPlayer(gameState: GameState): number {
	const twoClubs = gameState.deck.find((c) => getCardString(c) === '2C')!;
	const movePile = decodeMovePile(twoClubs.pile);
	const { player: position } = movePile!;
	return position;
}

function handleTrickEnded(gameState: GameState): GameState {
	gameState.roundStage = RoundStage.TrickEnd;
	const currentRound = gameState.rounds.at(-1);

	if (!currentRound) {
		return gameState;
	}

	const previousTrick = currentRound.tricks.at(-1);

	if (!previousTrick) {
		return gameState;
	}

	return gameState;
}

function handleNextStage(gameState: GameState): GameState {
	switch (gameState.roundStage) {
		case RoundStage.Deal:
		case RoundStage.Pass:
		case RoundStage.Trick:
		case RoundStage.GameOver:
			return gameState;
		case RoundStage.PassEnd:
			gameState = handleFinishPass(gameState);
			gameState.roundStage = RoundStage.Trick;
			break;
		case RoundStage.RoundEnd:
			gameState.heartsBroken = false;
			gameState.roundStage = RoundStage.Deal;
			gameState = action(GameAction.Deal, gameState);
			break;
		case RoundStage.TrickEnd:
			gameState = handleNextTrick(gameState);
			break;

	}

	return gameState;
}

function handleNextTrick(gameState: GameState): GameState {
	const currentRound = gameState.rounds.at(-1);

	if (!currentRound) {
		return gameState;
	}

	const previousTrick = currentRound.tricks.at(-1);

	if (!previousTrick) {
		return gameState;
	}

	const trickWinner = getTrickWinner(previousTrick);

	for (const card of previousTrick.cardsPlayed) {
		const deckCard = gameState.deck.find((c) => getCardString(c) === card);

		if (deckCard) {
			deckCard.pile = getScorePileName(trickWinner);
		}
	}

	if (!isRoundOver(gameState)) {
		gameState.currentPlayer = trickWinner;
		gameState.roundStage = RoundStage.Trick;
		currentRound.tricks.push(createTrick(trickWinner));
	} else {
		const calculatedScore = calculateCardPoints(gameState.deck);
		
		let isOverThreshold = false;

		for (let x = 0; x < gameState.playerCount; ++x) {
			const playerRoundScore = calculatedScore[x] ?? 0;

			currentRound.score[x] = playerRoundScore;

			const calculatedScoreHearts = calculateCardPointsHeartsOnly(gameState.deck);
			const playerRoundScoreHearts = calculatedScoreHearts[x] ?? 0;
			currentRound.heartPoints[x] = playerRoundScoreHearts;
			currentRound.queenSpadesPoints[x] = checkForQueenSpades(gameState.deck, x) ? 13 : 0;
		}

		//if moonshot
		const playerWhoShotTheMoon = calculatedScore.findIndex((s) => s === 26);
		const moonShot = playerWhoShotTheMoon !== -1;

		if (moonShot) {
			for (let x = 0; x < gameState.playerCount; ++x) {
				currentRound.heartPoints[x] = 0;
				currentRound.queenSpadesPoints[x] = 0;
				currentRound.moonPoints[x] = 26;
				currentRound.score[x] = 26;

				if (x === playerWhoShotTheMoon) {
					currentRound.moonPoints[x] = 0;
					currentRound.score[x] = 0;
					currentRound.moonShooter = x;
				}
			}
		}

		//add round score to the match score
		for (let x = 0; x < gameState.playerCount; ++x) {
			const playerRoundScore = currentRound.score[x] ?? 0;

			gameState.score[x] = (gameState.score[x] ?? 0) + playerRoundScore;

			if (gameState.score[x] >= gameState.scoreThreshold) {
				isOverThreshold = true;
			}
		}

		gameState.roundStage = isOverThreshold ? RoundStage.GameOver : RoundStage.RoundEnd;

	}

	return gameState;
}

function handleFinishPass(gameState: GameState): GameState {
	if (gameState.roundStage !== RoundStage.PassEnd) {
		return gameState;
	}

	for (let x = 0; x < gameState.playerCount; ++x) {
		const playerToPassTo = getPassPositionByGameState(gameState, x);
		const handToPassTo = getPlayerHandName(playerToPassTo);
		const passHandName = getPlayerPassHandName(x);

		for (const card of gameState.deck) {
			if (card.pile === passHandName) {
				card.pile = handToPassTo;
			}

			card.isSelected = false;
		}
	}

	for (const card of gameState.deck) {
		if (card.pile.indexOf('hand') < 0) {
			throw new Error('Card not passed back!');
		}
	}

	for (let x = 0; x < gameState.playerCount; ++x) {
		const cardsInHand = getCardsInHand(gameState.deck, x);

		if (cardsInHand.length !== 13) {
			throw new Error('Incorrect game state');
		}
	}

	gameState.currentPlayer = get2CPlayer(gameState);
	gameState.rounds.push(getNewRound(gameState.currentPlayer));
	return gameState;
}

function handleToggleCardAction(data: PlayCardData, gameState: GameState): GameState {
	const { card: cardString } = data;
	const card = gameState.deck.find((c) => getCardString(c) === cardString);

	if (!card) {
		return gameState;
	}

	card.isSelected = !card.isSelected;

	return gameState;
}

function getNewRound(currentPlayer: number): GameRound {
	const gameRound = createGameRound();
	gameRound.tricks.push(createTrick(currentPlayer));
	return gameRound;
}

//TODO: This begs the question, should the Rules just refer to this or should this refer to the Rules? Right now, it's just duplicating the logic
export function getValidActions(gameState: GameState, position: number): Command[] {
	switch (gameState.roundStage) {
		case RoundStage.Pass:
			return getValidPassActions(gameState, position);
		case RoundStage.Trick:
			return getValidTrickActions(gameState, position);
		default:
			return [];
	}
}

function getValidPassActions(gameState: GameState, position: number): Command[] {
	//if the player has already set passed cards, they can't pass any more

	const cardsAlreadyPassed = getCardsInPile(gameState.deck, getPlayerPassHandName(position));

	if (cardsAlreadyPassed?.length > 2) {
		return [];
	}

	const cards = sortCards(getCardsInHand(gameState.deck, position));
	const commands: Command[] = [];

	for (let x = 0; x < cards.length; ++x) {
		const card1 = cards[x];

		for (let y = x + 1; y < cards.length; ++y) {
			const card2 = cards[y];

			for (let z = y + 1; z < cards.length; ++z) {
				const card3 = cards[z];
				commands.push(makePassCommand(position, [getCardString(card1), getCardString(card2), getCardString(card3)]));
			}
		}
	}

	return commands;
}

function getValidTrickActions(gameState: GameState, position: number): Command[] {
	const actions: Command[] = [];
	const cards = sortCards(getCardsInHand(gameState.deck, position));

	for (const card of cards) {
		const command = makePlayCardCommand(position, getCardString(card));

		if (Rules.can(command, gameState)) {
			actions.push(command);
		}
	}

	return actions;
}
